import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, mergeMap, of } from 'rxjs';
import { MileagePlanProfileLookupStatus } from '../../../dtos/response/mileage-plan-profile-lookup-response/mileage-plan-profile-lookup-status';
import { AirlineCode } from '../../../models/airlines/airline-code';
import { MessageKey } from '../../../models/message/message-key';
import { RootState } from '../../../state/state';
import { MileagePlanMessageConverter } from '../../../utils/message-converters/mileage-plan/mileage-plan-message-converter';
import { addMultipleJetStreamMessagesNoDuplicate } from '../../message-service/state/message.actions';
import { MileagePlanProfileService } from '../../mileage-plan-profile-service/mileage-plan-profile.service';
import {
  mileagePlanAutoFillProfileSearch,
  mileagePlanProfileSearchByPassenger,
  mileagePlanProfileSearchByPaxComplete,
} from './mileage-plan-lookup-by-pax-service.actions';

@Injectable()
export class MileagePlanLookupByPaxServiceEffects {
  private readonly actions$ = inject(Actions);
  private readonly mileagePlanProfileService = inject(MileagePlanProfileService);
  private readonly mileagePlanMessageConverter = inject(MileagePlanMessageConverter);
  private readonly store = inject<Store<RootState>>(Store);

  mileagePlanAutoFillProfileSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(mileagePlanAutoFillProfileSearch),
      exhaustMap((action) => {
        return this.mileagePlanProfileService.mileagePlanProfileLookup(action.mileagePlanNumber, AirlineCode.AS, false).pipe(
          map((result) => {
            // Convert error messages
            const messageKeys = this.mileagePlanMessageConverter.convertMileagePlanLookupMessage(result, true);
            this.store.dispatch(addMultipleJetStreamMessagesNoDuplicate(messageKeys ?? [], undefined, { passengerId: action.passengerId }));

            return mileagePlanProfileSearchByPaxComplete(result, action.passengerHashId, true);
          }),
          catchError(() => {
            this.store.dispatch(
              addMultipleJetStreamMessagesNoDuplicate([MessageKey.AUTO_FILL_MP_FULL_FAILURE], undefined, {
                passengerId: action.passengerId,
              })
            );
            return of(
              mileagePlanProfileSearchByPaxComplete({ status: MileagePlanProfileLookupStatus.UNCAUGHT }, action.passengerHashId, true)
            );
          })
        );
      })
    )
  );

  mileagePlanProfileSearchByPassenger$ = createEffect(() =>
    this.actions$.pipe(
      ofType(mileagePlanProfileSearchByPassenger),
      mergeMap((action) => {
        return this.mileagePlanProfileService.mileagePlanProfileLookup(action.mileagePlanNumber, AirlineCode.AS, true).pipe(
          map((result) => {
            // Convert error messages
            if (action.showError) {
              const messageKeys = this.mileagePlanMessageConverter.convertMileagePlanLookupMessage(result, false);
              this.store.dispatch(
                addMultipleJetStreamMessagesNoDuplicate(messageKeys ?? [], undefined, { passengerId: action.passengerId })
              );
            }

            return mileagePlanProfileSearchByPaxComplete(result, action.passengerHashId);
          }),
          catchError(() => {
            if (action.showError) {
              this.store.dispatch(
                addMultipleJetStreamMessagesNoDuplicate([MessageKey.MP_LOOKUP_NOT_FOUND], undefined, {
                  passengerId: action.passengerId,
                })
              );
            }
            return of(mileagePlanProfileSearchByPaxComplete({ status: MileagePlanProfileLookupStatus.UNCAUGHT }, action.passengerHashId));
          })
        );
      })
    )
  );
}
